import { TieDetailsService } from './../../../ties/tie-details/tie-details.service';
import { Component, computed, inject, signal } from '@angular/core';
import { Observable, Subject, map, startWith, tap, filter, switchMap, of, interval, timer, takeUntil } from 'rxjs';
import { League } from '../../../_shared/models/league.model';
import { LeagueService } from '../../league.service';
import { ActivatedRoute, Params } from '@angular/router';
import { Tie } from '../../../_shared/models/tie.model';
import { Socket } from 'ngx-socket-io';
import { toSignal } from '@angular/core/rxjs-interop';

interface Config {
  side: string;
  _tieId: string | undefined;
}

@Component({
  standalone: false,
  selector: 'app-split-videoboard',
  templateUrl: './split-videoboard.component.html',
  styleUrls: ['./split-videoboard.component.scss'],
})
export class SplitVideoboardComponent {
  private leagueService = inject(LeagueService);
  private activatedRoute = inject(ActivatedRoute);
  private tieDetailService = inject(TieDetailsService);
  private socket = inject(Socket);

  private refreshLeague$ = new Subject<void>();
  private refreshLeftSideTie$ = new Subject<void>();
  private refreshRightSideTie$ = new Subject<void>();

  league = signal(undefined);
  leftSideTieId: string | undefined;
  rightSideTieId: string | undefined;

  league$: Observable<League> = this.refreshLeague$.pipe(
    startWith(undefined),
    switchMap(() => this.activatedRoute.params),
    switchMap((params: Params) => this.leagueService.getLeague(params['leagueId'], false)),
    map(res => res.data),
    tap((league: League) => {
      this.socket.emit('join-room', league._id);
      this.leftSideTieId = league.settings.splitVideoBoard.leftSideTie?._id;      
      this.rightSideTieId = league.settings.splitVideoBoard.rightSideTie?._id;
      this.socketSubscriptions();
    })
  )

  loaded = computed(() => {    
    return !!this.league()
  })

  leftSideTieInfo$: Observable<Tie> = this.refreshLeftSideTie$.pipe(
    startWith(undefined),
    filter(() => this.leftSideTieId !== null),
    switchMap(() => {
      if (this.leftSideTieId) {
        return this.tieDetailService.getTieInfo(this.leftSideTieId, false, false, '');
      } else {
        return of(undefined)
      }
    }),
    tap((tie) => {
      if (tie) {        
        this.socket.emit('join-room', this.leftSideTieId);
      }
    })
  )

  leftSideTieInfo = toSignal(this.leftSideTieInfo$)

  rightSideTieInfo$: Observable<Tie> = this.refreshRightSideTie$.pipe(
    startWith(undefined),
    filter(() => this.rightSideTieId !== null),
    switchMap(() => {
      if (this.rightSideTieId) {
        return this.tieDetailService.getTieInfo(this.rightSideTieId, false, true, '')
      } else {
        return of(undefined)
      }
    }),
    tap((tie) => {
      if (tie) {
        this.socket.emit('join-room', this.rightSideTieId);
      }
    })
  )

  rightSideTieInfo = toSignal(this.rightSideTieInfo$)
  private readonly destroy$: Subject<void> = new Subject();

  constructor() {
    this.league$.subscribe(res => {
      this.league.set(res);
      if (this.leftSideTieId) {
        this.refreshLeftSideTie$.next()
      }

      if (this.rightSideTieId) {
        this.refreshRightSideTie$.next()
      }
    })

    interval(3 * 60 * 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.doRefresh()
      })
  }

  doRefresh() {    
    this.refreshLeftSideTie$.next();
    this.refreshRightSideTie$.next();
  }
  
  private socketSubscriptions() {
    this.updateSplitVideoBoard()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
            (config: Config) => {              
              if (config.side === 'LEFT') {
                if (!config._tieId) {
                  this.socket.emit('leave-room', this.leftSideTieId);
                }
                this.leftSideTieId = config._tieId
                this.refreshLeftSideTie$.next();
              } else {
                if (!config._tieId) {
                  this.socket.emit('leave-room', this.rightSideTieId);
                }
                this.rightSideTieId = config?._tieId;
                this.refreshRightSideTie$.next();
              }
            }
        );

    this.getTieStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
          (status: any) => {
            if (this.leftSideTieId === status._tieId) {
              this.leftSideTieInfo().score = status.score
              this.leftSideTieInfo().winningSide = status.winningSide
              this.leftSideTieInfo().matchStatus = status.matchStatus  
            }

            if (this.rightSideTieId === status._tieId) {
              this.rightSideTieInfo().score = status.score
              this.rightSideTieInfo().winningSide = status.winningSide
              this.rightSideTieInfo().matchStatus = status.matchStatus  
            }
          }
      );
}

  private updateSplitVideoBoard() {
    const observable = new Observable(
      observer => {
        this.socket.on(
          'update-split-videoboard',
          data => observer.next(data)
        );
      });
    return observable;
  }

  private getTieStatus() {
    const observable = new Observable(
      observer => {
        this.socket.on(
          'tie-status-updated',
          data => observer.next(data)
        );
      });
    return observable;
  }
}
